import Choices from "choices.js";

// Imports for typing
/* eslint-disable no-unused-vars */
import {TilesData, Surface} from "./tiles-data";
import Choice from "choices.js";
/* eslint-enable no-unused-vars */

export class TileSelectionComponent {
  /** @type {TilesData} */  data;
  /** @type {HTMLElement} */ parentElement;
  /** @type {HTMLElement} */ wrapperElement;
  /** @type {HTMLElement} */ imageElement;
  /** @type {HTMLElement} */ productInputElement;
  /** @type {Choices} */ productInput;
  /** @type {HTMLElement} */ surfaceInputElement;
  /** @type {Choices} */ surfaceInput;
  /** @type {(self: TileSelectionComponent, removeSelection?: boolean) => {}} */ onSelectCallback;
  /** @type {Surface} */ currentSurfaceSelection;
  /** @type {string} */ tilePlaceholderImageUrl = '/wp-content/themes/ContexLabs/assets/images/sample-tile-order/sample-tile-placeholder.png';

  /**
   * Initialize and draw one TileSelectionComponent.
   *
   * @param {TilesData} data The data containing all tile and surface information.
   * @param {HTMLElement} rootElement The root element, where all TileSelectionComponents should be rendered.
   * @param {(self: TileSelectionComponent, removeSelection?: boolean) => {}} onSelectCallback
   */
  constructor(data, rootElement, onSelectCallback) {
    this.data = data;
    this.parentElement = rootElement;
    this.onSelectCallback = onSelectCallback;

    this._generateHtml();

    const tileChoices = this._getTileChoices();

    this.productInput = new Choices(this.productInputElement, {
      choices: tileChoices,
      itemSelectText: '',
    });
    this.surfaceInput = new Choices(this.surfaceInputElement, {
      itemSelectText: '',
    });
    this.surfaceInput.disable();

    this.productInputElement.addEventListener('choice', (event) => {
        this.setProductSelection(event.detail.choice.customProperties.categoryKey, event.detail.choice.value);
    });

    this.surfaceInputElement.addEventListener('choice', (event) => {
      this.setSurfaceSelection(event.detail.choice.customProperties);
    });
  }

  /**
   * @returns {Surface}
   */
  getSelection() {
    return this.currentSurfaceSelection;
  }

  /**
   * @param {string} categoryKey
   * @param {string} tileKey
   */
  setProductSelection(categoryKey, tileKey) {
    this.productInput.setChoiceByValue(tileKey); // Needed when setProductSelection is used from the outside
    const surfaceChoices = this._getSurfaceChoices(categoryKey, tileKey);
    this.surfaceInput.enable();
    this.surfaceInput.clearStore();
    this.surfaceInput.setChoices(surfaceChoices);
    this.imageElement.setAttribute('src', this.tilePlaceholderImageUrl);
    this.currentSurfaceSelection = undefined;
    this.onSelectCallback();
  }

  /**
   * @param {Surface} surface
   */
  setSurfaceSelection(surface) {
    this.surfaceInput.setChoiceByValue(surface.post_name); // Needed when setSurfaceSelection is used from the outside
    this.imageElement.setAttribute('src', surface.img);
    this.currentSurfaceSelection = surface;
    this.onSelectCallback(this);
  }

  /**
   * Get all tiles and tile categories from data and create array for choices.js.
   *
   * @returns {Choice[]}
   */
  _getTileChoices() {
    const tileCategoryNames = this.data.tax.ziegel_kategorie;
    const tileCategories = new Map(Object.entries(this.data.ziegel));

    const groupedChoices = [];

    tileCategories.forEach((category, categoryKey) => {
      const choices = Object.values(category)
        .filter((tile) => !!tile.child) // Remove all tiles without surfaces
        .map((tile) => ({
          label: tile.title,
          value: tile.post_name,
          customProperties: {
            categoryKey,
          },
        }));

      groupedChoices.push({
        label: tileCategoryNames[categoryKey].name,
        choices,
      });
    });

    return groupedChoices;
  }

  /**
   * Get all surfaces and surface categories from data and create array for choices.js.
   *
   * @returns {Choice[]}
   */
  _getSurfaceChoices(tileCategoryKey, tileKey) {
    const surfaceNames = this.data.tax.oberflache;
    const surfaceCategoriesObject = this.data.ziegel[tileCategoryKey][tileKey].child;
    const surfaceCategories = new Map(Object.entries(surfaceCategoriesObject));

    const groupedSurfaces = [];

    surfaceCategories.forEach((category, surfaceCategoryKey) => {
      const choices = Object.values(category).map((surface) => ({
        label: surface.title,
        value: surface.post_name,
        customProperties: surface,
      }));

      groupedSurfaces.push({
        label: surfaceNames[surfaceCategoryKey].name,
        choices,
      });
    });

    return groupedSurfaces;
  }

  _generateHtml() {
    this.wrapperElement = this._createElementWith('div', 'widget-sample-tile-selection__element');
    const containerElement = this._createElementWith('div', 'widget-sample-tile widget-sample-tile--selection');
    this.wrapperElement.appendChild(containerElement);

    const imageContainer = this._createElementWith('div', 'widget-sample-tile__image');
    this.imageElement = document.createElement('img');
    this.imageElement.setAttribute('src', this.tilePlaceholderImageUrl);
    // this.imageElement.setAttribute('srcset', '/assets/images/sample-tile-order/sample-tile-placeholder.png 1x, /assets/images/sample-tile-order/sample-tile-placeholder@2x.png 2x');
    imageContainer.appendChild(this.imageElement);

    const formContainer = this._createElementWith('div', 'widget-sample-tile__form');
    const productInputContainer = document.createElement('div');
    productInputContainer.className = 'widget-sample-tile__field-group';
    productInputContainer.innerHTML = '<label class="form-label" for="choices-product">Dachziegel</label>';
    this.productInputElement = this._createElementWith('select', 'form-control', 'choices-product', 'choices-product');
    this.productInputElement.setAttribute('data-trigger', '');
    productInputContainer.appendChild(this.productInputElement);

    const surfaceInputContainer = document.createElement('div');
    surfaceInputContainer.className = 'widget-sample-tile__field-group';
    surfaceInputContainer.innerHTML = '<label class="form-label" for="choices-surface">Oberfläche / Farbe</label>';
    this.surfaceInputElement = this._createElementWith('select', 'form-control', 'choices-surface', 'choices-surface');
    this.surfaceInputElement.setAttribute('data-trigger', '');
    surfaceInputContainer.appendChild(this.surfaceInputElement);

    const removeButtonElement = this._createElementWith('button', 'button button--supplemental');
    removeButtonElement.innerHTML = '<svg class="icon icon--before"><use xlink:href="#icon-close"></use></svg> Ziegel entfernen';
    removeButtonElement.addEventListener('click', () => this._destroy());

    formContainer.append(productInputContainer, surfaceInputContainer);
    containerElement.append(imageContainer, formContainer, removeButtonElement);
    this.parentElement.appendChild(this.wrapperElement);
  }

  _destroy() {
    this.productInput.destroy();
    this.surfaceInput.destroy();
    this.parentElement.removeChild(this.wrapperElement);
    this.onSelectCallback(this, true);
  }

  /**
   * @param {string} tagName
   * @param {string} [className]
   * @param {string} [id]
   * @param {string} [name]
   * @returns {HTMLElement}
   */
  _createElementWith(tagName, className, id, name) {
    const element = document.createElement(tagName);
    if (className)
      element.className = className;
    if (id)
      element.id = id;
    if (name)
      element.name = name;
    return element;
  }
}
